import type { AllPagesKeys, PagesEnum, SubPagesType } from '@common/enums/pages.enum'
import type { PageDetails } from '@omnia/ui'
import type { FunctionComponent } from 'react'
import { createElement } from 'react'
import type { RouteObject } from 'react-router-dom-v7'
import { Navigate } from 'react-router-dom-v7'
import { Pages } from '@/content/pages/pages'
import { cmsDataService } from '@/content/services/cms.data.service'
import { navigationService } from '@/content/services/navigation.service'
import { PageWrapper } from './PageWrapper'
import { SubPageWrapper } from './SubPageWrapper'

// eslint-disable-next-line complexity -- to be refactored at a later time
export const getRoutes = (overridePageComponent?: { pageKey: AllPagesKeys; component: FunctionComponent }) => {
    const routes: RouteObject[] = []
    for (const pageKey of navigationService.PagesNames(true)) {
        const overrideElement = pageKey === overridePageComponent?.pageKey ? overridePageComponent.component : undefined

        const page = Pages[pageKey]

        // The "urls" property is undefined if a react router page does not have a corresponding umbraco page setup.
        if (!page?.urls?.length) {
            continue
        }

        const pageRoute: RouteObject = {
            element: <PageWrapper page={pageKey} overrideElement={overrideElement} />,
        }

        if (!page?.subPages?.length) {
            for (const url of page.urls) {
                routes.push({ ...pageRoute, path: url })
            }

            continue
        }

        pageRoute.children = []
        for (const subPage of page.subPages) {
            const overrideSubPageComponent =
                subPage.alias === overridePageComponent?.pageKey ? overridePageComponent?.component : undefined

            if (!subPage?.urls?.length) {
                continue
            }

            subPage.urls?.forEach(url => {
                pageRoute.children!.push({
                    element: (
                        <SubPageWrapper page={subPage.alias!}>
                            {createElement(overrideSubPageComponent ?? subPage.component)}
                        </SubPageWrapper>
                    ),
                    path: url.replace(navigationService.Pages[pageKey].urls![0], ''),
                })
            })
        }

        // When no subpage was found, go to first one

        const firstSubPageUrl = getFirstAvailableSubPageUrlFromCmsPageOrder(page)
        pageRoute.children.push({
            element: <Navigate to={firstSubPageUrl ?? '/'} replace={true} />,
            path: '*',
        })

        for (const url of page.urls) {
            routes.push({ ...pageRoute, path: url + '/*' })
        }
    }

    // Not found routes work as you'd expect
    routes.push({
        path: '*',
        element: <PageWrapper page={'ErrorNotFound'} />,
    })

    return routes
}

/**
 * Relies on the "pages" initial data object returning the aliases in the order they are defined in the CMS content tree.
 */
const getFirstAvailableSubPageUrlFromCmsPageOrder = (page: PageDetails<PagesEnum, SubPagesType>) => {
    if (!page.subPages?.length) {
        return undefined
    }

    const subPageKeys = page.subPages.map(subPage => subPage.alias?.toString())

    const orderedInitialDataPageKeys = Object.keys(cmsDataService.get().pages)

    const orderedSubPageKeys = orderedInitialDataPageKeys.filter(key => !!key && subPageKeys.includes(key))

    const firstSubPageKeyWithUrl = orderedSubPageKeys?.find(key => !!cmsDataService.get().pages[key]?.[0])

    const firstSubPageUrl = firstSubPageKeyWithUrl ? cmsDataService.get().pages[firstSubPageKeyWithUrl]?.[0] : undefined

    return firstSubPageUrl
}
